import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { OperationProgressModel } from '../../../shared/ui/progress';
import {
  buildProfileApplyWindowModel,
  stabilizeProfileApplyWindowModel,
} from '../lib/mappingProfileApplyProgressWindowModel';
import type { MappingProjectReadinessResult } from '../lib/mappingProjectReadiness';
import type { MappingProfileApplyProgressState } from './profileControllerShared';

interface ProfileApplyProgressWindowSession {
  operationId: string;
  profileProgress: MappingProfileApplyProgressState;
}

const ReadyCloseDwellMs = 1_200;

export interface UseMappingProfileApplyProgressWindowArgs {
  profileApplyProgress: MappingProfileApplyProgressState | null;
  readinessProgressModel: OperationProgressModel | null;
  readinessStatus: string;
  readinessBlockReason?: MappingProjectReadinessResult['blockReason'] | null;
  readinessMessage: string | null;
  resolvedProfileName: string;
  profileSessionPending?: boolean;
  projectProfileGateOpen?: boolean;
}

export interface MappingProfileApplyProgressWindowState {
  open: boolean;
  model: OperationProgressModel | null;
  operationId: string | null;
  clearSession: () => void;
}

function buildProfileApplyOperationId(progress: MappingProfileApplyProgressState): string {
  return `mapping-profile-apply:${progress.profileKey}:${progress.startedAt}`;
}

function isSameProfileApplySession(
  session: ProfileApplyProgressWindowSession,
  progress: MappingProfileApplyProgressState
): boolean {
  return (
    session.profileProgress.profileKey === progress.profileKey &&
    session.profileProgress.startedAt === progress.startedAt
  );
}

function isReadyToCloseCompletedSession(input: {
  profileProgress: MappingProfileApplyProgressState | null;
  readinessStatus: string;
  profileSessionPending: boolean;
}): boolean {
  return (
    input.profileProgress?.phase === 'complete' &&
    input.readinessStatus === 'ready' &&
    !input.profileSessionPending
  );
}

function shouldReleaseForReadinessBlock(
  readinessStatus: string,
  readinessBlockReason: MappingProjectReadinessResult['blockReason'] | null | undefined
): boolean {
  return (
    readinessStatus === 'blocked' &&
    (readinessBlockReason === 'profileConfirmation' ||
      readinessBlockReason === 'familyRepairRequired' ||
      readinessBlockReason === 'projectInspection')
  );
}

export function useMappingProfileApplyProgressWindow({
  profileApplyProgress,
  readinessProgressModel,
  readinessStatus,
  readinessBlockReason,
  readinessMessage,
  resolvedProfileName,
  profileSessionPending = false,
  projectProfileGateOpen = false,
}: UseMappingProfileApplyProgressWindowArgs): MappingProfileApplyProgressWindowState {
  const [session, setSession] = useState<ProfileApplyProgressWindowSession | null>(null);
  const [readinessSettledOperationId, setReadinessSettledOperationId] = useState<string | null>(null);
  const [readyToCloseOperationId, setReadyToCloseOperationId] = useState<string | null>(null);
  const visibleModelRef = useRef<OperationProgressModel | null>(null);

  useEffect(() => {
    if (!profileApplyProgress) {
      return;
    }

    setSession((current) => {
      if (current && isSameProfileApplySession(current, profileApplyProgress)) {
        return {
          ...current,
          profileProgress: profileApplyProgress,
        };
      }

      return {
        operationId: buildProfileApplyOperationId(profileApplyProgress),
        profileProgress: profileApplyProgress,
      };
    });
  }, [profileApplyProgress]);

  const activeProfileProgress = profileApplyProgress ?? session?.profileProgress ?? null;
  const operationId = activeProfileProgress
    ? session?.operationId ?? buildProfileApplyOperationId(activeProfileProgress)
    : session?.operationId ?? null;
  const sessionReadinessProgressModel =
    activeProfileProgress?.phase === 'complete'
      ? readinessProgressModel
      : null;
  const awaitingFreshReadiness =
    activeProfileProgress?.phase === 'complete' &&
    readinessStatus === 'ready' &&
    operationId !== null &&
    readinessSettledOperationId !== operationId;
  const profileWorkflowPending = profileSessionPending || awaitingFreshReadiness;
  const closeCompletedSession = isReadyToCloseCompletedSession({
    profileProgress: activeProfileProgress,
    readinessStatus,
    profileSessionPending: profileWorkflowPending,
  });
  const readyCloseSettled =
    closeCompletedSession &&
    operationId !== null &&
    readyToCloseOperationId === operationId;

  useEffect(() => {
    if (
      !operationId ||
      activeProfileProgress?.phase !== 'complete' ||
      readinessStatus !== 'ready' ||
      profileSessionPending
    ) {
      setReadinessSettledOperationId((current) =>
        current === operationId ? null : current
      );
      return undefined;
    }

    const timeoutId = globalThis.setTimeout(() => {
      setReadinessSettledOperationId(operationId);
    }, 0);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [activeProfileProgress?.phase, operationId, profileSessionPending, readinessStatus]);

  useEffect(() => {
    if (!operationId) {
      setReadyToCloseOperationId(null);
      return;
    }

    setReadyToCloseOperationId((current) =>
      current && current !== operationId ? null : current
    );
  }, [operationId]);

  useEffect(() => {
    if (!closeCompletedSession || !operationId) {
      setReadyToCloseOperationId((current) =>
        current === operationId ? null : current
      );
      return undefined;
    }

    const timeoutId = globalThis.setTimeout(() => {
      setReadyToCloseOperationId(operationId);
    }, ReadyCloseDwellMs);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [closeCompletedSession, operationId]);

  useEffect(() => {
    if (readyCloseSettled) {
      setSession(null);
    }
  }, [readyCloseSettled]);

  const releaseForReadinessBlock = shouldReleaseForReadinessBlock(
    readinessStatus,
    readinessBlockReason
  );

  const model = useMemo(() => {
    if (projectProfileGateOpen || readyCloseSettled || !activeProfileProgress) {
      visibleModelRef.current = null;
      return null;
    }

    if (releaseForReadinessBlock) {
      visibleModelRef.current = null;
      return null;
    }

    const rawModel = buildProfileApplyWindowModel({
      operationId: operationId ?? undefined,
      profileApplyProgress: activeProfileProgress,
      readinessProgressModel: sessionReadinessProgressModel,
      readinessStatus,
      readinessMessage,
      resolvedProfileName,
      profileSessionPending: profileWorkflowPending,
    });
    const stabilizedModel = stabilizeProfileApplyWindowModel(
      visibleModelRef.current,
      rawModel
    );
    visibleModelRef.current = stabilizedModel;

    return stabilizedModel;
  }, [
    activeProfileProgress,
    operationId,
    profileWorkflowPending,
    projectProfileGateOpen,
    readyCloseSettled,
    readinessMessage,
    sessionReadinessProgressModel,
    readinessStatus,
    resolvedProfileName,
    releaseForReadinessBlock,
  ]);

  const clearSession = useCallback(() => {
    setSession(null);
    setReadinessSettledOperationId(null);
    setReadyToCloseOperationId(null);
    visibleModelRef.current = null;
  }, []);

  return {
    open: Boolean(model),
    model,
    operationId,
    clearSession,
  };
}
